import React from "react";
import { Form, Input, InputNumber, Spin, FormInstance, Select } from "antd";
import { Customer } from "../../Types";
import { DRAWER_FORM_WIDTH, drawerFooter, drawerTitle } from "../AppUi";
import { ResizableDrawer } from "../ResizableDrawer/ResizableDrawer";

interface Props {
  open: boolean;
  editingCustomer: Customer | null;
  form: FormInstance;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: () => void;
  routes: any[];
  routesLoading: boolean;
}

const { Option } = Select;

export const CustomerFormDrawer: React.FC<Props> = ({
  open,
  editingCustomer,
  form,
  isSubmitting,
  onClose,
  onSubmit,
  routes,
  routesLoading,
}) => (
  <ResizableDrawer
    title={drawerTitle(editingCustomer ? "Edit Customer" : "Add New Customer")}
    open={open}
    onClose={onClose}
    width={DRAWER_FORM_WIDTH}
    footer={drawerFooter({
      actions: [
        { label: "Cancel", onClick: onClose },
        {
          label: editingCustomer ? "Save Changes" : "Create Customer",
          type: "primary",
          loading: isSubmitting,
          onClick: onSubmit,
        },
      ],
    })}
  >
    <Spin spinning={isSubmitting}>
      <Form form={form} layout="vertical">
        <Form.Item
          label="Full Name"
          name="name"
          rules={[{ required: true, message: "Please enter customer name." }]}
        >
          <Input placeholder="Enter full name" />
        </Form.Item>

        <Form.Item
          label="Phone Number"
          name="phone"
          rules={[
            { required: true, message: "Please enter phone number." },
            {
              pattern: /^[0-9+-\s()]*$/,
              message: "Please enter a valid phone number.",
            },
          ]}
        >
          <Input placeholder="Enter phone number" />
        </Form.Item>

        <Form.Item
          label="Email Address (Optional)"
          name="email"
          rules={[
            {
              type: "email",
              message: "Please enter a valid email address.",
            },
          ]}
        >
          <Input placeholder="Enter email address" />
        </Form.Item>

        <Form.Item label="Physical Address (Optional)" name="address">
          <Input.TextArea
            placeholder="Enter address details"
            rows={3}
            style={{ resize: "none" }}
          />
        </Form.Item>

        <Form.Item
          label="Credit Limit"
          name="credit_limit"
          rules={[
            {
              type: "number",
              min: 0,
              message: "Credit limit must be a positive number.",
            },
          ]}
        >
          <InputNumber
            placeholder="Enter credit limit"
            className="w-full"
            min={0}
            style={{ width: "100%" }}
          />
        </Form.Item>

        {/* Assigned Route */}
        <Form.Item
          label={
            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              Assigned Route
            </span>
          }
          name="route_id"
          extra="Select the route assigned to this customer."
        >
          <Select
            showSearch
            allowClear
            loading={routesLoading}
            placeholder="Select routes"
            className="rounded-xl"
            optionFilterProp="label"
            maxTagCount="responsive"
            notFoundContent={
              routesLoading ? "Loading routes..." : "No routes available"
            }
          >
            {(routes ?? []).map((route) => (
              <Option key={route.id} value={route.id} label={route.name}>
                <div className="flex items-center gap-2">
                  <span>{route.name}</span>
                </div>
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Spin>
  </ResizableDrawer>
);
